import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/** Mirrors ReviewCreditStatus in cleanse_api/models/billing/review_credit.py. */
export type ReviewCreditStatus = 'pending' | 'approved' | 'rejected';

/** Mirrors ReviewCreditResponse in
 *  cleanse_api/api/billing/review_credits.py. */
export interface ReviewCreditRequest {
  id: string;
  organization_id: string;
  user_id: string;
  review_url: string;
  status: ReviewCreditStatus;
  credits_awarded: number;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string | null;
}

/** Mirrors ReviewCreditApproveResponse. */
export interface ReviewCreditApproveResponse {
  request: ReviewCreditRequest;
  credits_granted: number;
  new_balance: number;
}

export const reviewCreditsApi = createApi({
  reducerPath: 'reviewCreditsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['ReviewCredit'],
  endpoints: (builder) => ({
    // The caller's org's latest request (or null if never submitted).
    getMyReviewCredit: builder.query<ReviewCreditRequest | null, void>({
      query: () => '/review-credits/me',
      providesTags: [{ type: 'ReviewCredit', id: 'MINE' }],
    }),

    // Submit the AppSumo review URL. 409 if the org already has a
    // pending/approved request; 400 if the URL isn't a valid review link.
    submitReviewCredit: builder.mutation<
      ReviewCreditRequest,
      { review_url: string }
    >({
      query: (body) => ({
        url: '/review-credits',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ReviewCredit', id: 'MINE' }],
    }),

    // Staff-only (server gates on @cleanlist.ai → 403 otherwise).
    adminListPendingReviewCredits: builder.query<
      ReviewCreditRequest[],
      { limit?: number; offset?: number } | void
    >({
      query: (args) => ({
        url: '/review-credits/pending',
        params: {
          limit: args?.limit ?? 50,
          offset: args?.offset ?? 0,
        },
      }),
      providesTags: [{ type: 'ReviewCredit', id: 'PENDING' }],
    }),

    adminApproveReviewCredit: builder.mutation<
      ReviewCreditApproveResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/review-credits/${id}/approve`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'ReviewCredit', id: 'PENDING' }],
    }),

    adminRejectReviewCredit: builder.mutation<
      ReviewCreditRequest,
      { id: string; reason?: string }
    >({
      query: ({ id, reason }) => ({
        url: `/review-credits/${id}/reject`,
        method: 'POST',
        body: { reason: reason || null },
      }),
      invalidatesTags: [{ type: 'ReviewCredit', id: 'PENDING' }],
    }),
  }),
});

export const {
  useGetMyReviewCreditQuery,
  useSubmitReviewCreditMutation,
  useAdminListPendingReviewCreditsQuery,
  useAdminApproveReviewCreditMutation,
  useAdminRejectReviewCreditMutation,
} = reviewCreditsApi;
